import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useAppXrResource } from './useAppXrResource';

function StatusIcon({ ok }: { ok: boolean | undefined }) {
  if (ok === true)
    return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />;
  if (ok === false)
    return <ErrorIcon sx={{ color: '#f44336', fontSize: 18 }} />;
  return <HourglassEmptyIcon sx={{ color: '#ff9800', fontSize: 18 }} />;
}

function parseCommitSha(resolvedImage: string): string | undefined {
  const tag = resolvedImage.split(':').pop() ?? '';
  return tag.split('-').find(p => /^[a-f0-9]{7,40}$/.test(p));
}

export const AppStatusCard = () => {
  const { resource, conditions, error, loading } = useAppXrResource();
  const status = resource?.status;
  const sourceRepo = resource?.spec?.sourceRepo;

  if (error) {
    return (
      <Card>
        <CardHeader title="App Status" />
        <CardContent>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (loading || !status) {
    return (
      <Card>
        <CardHeader title="App Status" />
        <CardContent>
          <Typography>Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  const shortImage = status.resolvedImage
    ? status.resolvedImage.split('/').pop()
    : undefined;
  const commitSha = status.resolvedImage
    ? parseCommitSha(status.resolvedImage)
    : undefined;
  const commitUrl =
    commitSha && sourceRepo
      ? `https://github.com/${sourceRepo}/commit/${commitSha}`
      : undefined;

  const rowSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    py: 0.5,
  };
  const labelSx = { color: 'text.secondary', fontSize: '0.85rem' };
  const valueSx = { fontSize: '0.85rem', fontFamily: 'monospace' };

  return (
    <Card>
      <CardHeader title="App Status" />
      <CardContent>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Image</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                ...valueSx,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={status.resolvedImage || undefined}
            >
              {shortImage || 'Not resolved yet'}
            </Typography>
            {commitUrl && (
              <Link
                href={commitUrl}
                target="_blank"
                rel="noopener"
                sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
              >
                {commitSha?.slice(0, 7)}
              </Link>
            )}
          </Box>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Namespace</Typography>
          <Typography sx={valueSx}>{status.appNamespace ?? '—'}</Typography>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Deployment</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StatusIcon ok={status.deployment?.ready} />
            <Typography sx={valueSx}>
              {status.deployment?.availableReplicas ?? 0}/
              {status.deployment?.updatedReplicas ?? 0} replicas
            </Typography>
          </Box>
        </Box>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>Ingress</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StatusIcon ok={status.httproute?.accepted} />
            <Typography sx={valueSx}>
              {status.httproute?.accepted ? 'Accepted' : 'Pending'}
            </Typography>
          </Box>
        </Box>
        {conditions.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={labelSx} gutterBottom>
              Conditions
            </Typography>
            <Grid container spacing={1}>
              {conditions.map(c => (
                <Grid item key={c.type}>
                  <Chip
                    sx={{ m: 0.25 }}
                    size="small"
                    icon={<StatusIcon ok={c.status === 'True'} />}
                    label={`${c.type}: ${c.message ?? c.reason ?? c.status}`}
                    variant="outlined"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
